/* eslint-disable react/jsx-no-duplicate-props */
import { faTint } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button, DialogActions, DialogContent, InputAdornment, TextField
} from '@mui/material';
import {
  CustomIcon,
  CustomTooltip,
  Datepicker,
  FlexContainer,
  FormLine,
  Question,
  RequiredSelectHack,
  TextNoOptionsAvailable
} from 'components';
import { SkeletonForm } from 'components/Skeletons';
import debounce from 'debounce-promise';
import { useFetch, useStores } from 'hooks';
import { FluidMovementView } from 'models';
import React, { useCallback, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { FluidMovementsService, LegalEntityService, TrackDechetsService } from 'services';
import { DISTRIBUTOR_ROLES } from 'utils/constants';
import {
  DATE_FORMAT, formatDate, FormHelper, translate, UserHelper
} from 'utils/helpers';
import { SelectItem } from 'utils/types';

import { ModalHeader } from './ModalHeader';

export const CheckPendingMovementModal = ({
  onConfirm, onClose, closeOnSubmit, defaultValues, fluidOptions
}) => {
  const { fluidStore } = useStores();
  const isDistributor = UserHelper.hasAccessRight(DISTRIBUTOR_ROLES);

  const {
    response: tdConnectionStatus,
    isLoading: isCheckingStatus
  } = useFetch(() => TrackDechetsService.getConnectionStatus(), null, [], false);
  const [currentMovement, setCurrentMovement] = useState<FluidMovementView>();
  const [operationOptions, setOperationOptions] = useState<SelectItem[]>([]);
  const [searchSuppliersOrRecipients, setSearchSuppliersOrRecipients] = useState('');
  const [suppliersOrRecipients, setSuppliersOrRecipients] = useState<SelectItem[]>([]);
  const [isLoadingSuppliersOrRecipients, setIsLoadingSuppliersOrRecipients] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      ...defaultValues,
      movementDate: formatDate(new Date())
    }
  });

  const formValues = {
    bottle: watch('bottle'),
    fluid: watch('fluid'),
    supplierOrRecipient: watch('supplierOrRecipient'),
    operationCode: watch('operationCode'),
    nextRecipient: watch('nextRecipient'),
    movementDate: watch('movementDate'),
    transporterImat: watch('transporterImat'),
    comment: watch('comment'),
    measuredAmount: watch('measuredAmount')
  };

  const formattedfluidOptionsExtended = fluidStore.fluidExtendedOptions.map(fluid => ({
    value: fluid.id,
    label: fluid.name
  }));

  useEffect(() => {
    register('bottle');
    register('fluid', { required: true });
    register('supplierOrRecipient');
    register('operationCode', { required: true });
    register('nextRecipient');
    register('comment');
    register('measuredAmount', {
      required: true,
      min: currentMovement?.bsffIdentifier ? 1 : 0
    });
    register('transporterImat', {
      required: currentMovement?.movementType?.value === 'PENDING_TRANSPORT'
    });
  }, [register, currentMovement]);

  useEffect(() => {
    FluidMovementsService.getOperationOptions().then(setOperationOptions);
  }, []);

  useEffect(() => {
    LegalEntityService.getLegalEntitiesOptionsFiltered({ types: ['SUPPLIER'] })
        .then(setSuppliersOrRecipients)
        .finally(() => setIsLoadingSuppliersOrRecipients(false));
  }, []);

  useEffect(() => {
    if (defaultValues.hashId) {
      setIsLoading(true);

      FluidMovementsService.getFluidMovement(defaultValues.hashId)
          .then((resp) => {
            setCurrentMovement(resp);
            Object.entries(resp).forEach(([key, value]) => setValue(key, value));

            const selectedFluid = formattedfluidOptionsExtended.find(f => `${f.value}` === resp.fluid?.value);
            setValue('fluid', selectedFluid);
            setValue('measuredAmount', resp.measuredAmount ? resp.measuredAmount : resp.theoricalAmount);

            setValue('bottle', resp.bottle
                ? {
                  value: resp.bottle.id,
                  label: resp.bottle.identifier,
                  fluid: resp.bottle.fluid
                }
                : null);
          }).finally(() => setIsLoading(false));
    }
  }, [defaultValues.hashId, setValue, formattedfluidOptionsExtended]);

  const getAsyncSuppliersOrRecipientsOptions = useCallback(inputValue => new Promise((resolve) => {
    if (!inputValue) {
      setIsLoadingSuppliersOrRecipients(false);
      return;
    }
    setIsLoadingSuppliersOrRecipients(true);
    if (inputValue.length < 3) {
      return;
    }

    LegalEntityService.getLegalEntitiesOptionsFiltered({ freeSearch: inputValue, types: ['SUPPLIER'] })
        .then(resolve)
        .finally(() => setIsLoadingSuppliersOrRecipients(false));
  }), []);

  const debouncedSuppliersOrRecipientsOptions = debounce(getAsyncSuppliersOrRecipientsOptions, 500);

  const handleValidateModal = (data) => {
    const request = {
      ...data,
      hashId: currentMovement.hashId,
      fluid: FormHelper.SELECT_ADAPTOR_TO_BACK(data.fluid),
      operationCode: FormHelper.SELECT_ADAPTOR_TO_BACK(data.operationCode),
      nextRecipient: data.nextRecipient ? { hashId: data.nextRecipient.value } : null,
      theoricalAmount: Number(data.theoricalAmount),
      measuredAmount: Number(data.measuredAmount)
    };

    onConfirm(request);

    if (closeOnSubmit) {
      onClose();
    }
  };

  const displayNoOptionAvailable = useCallback((search) => {
    if (!search || search.length < 3) {
      return (
        <TextNoOptionsAvailable>{translate('modalFluidMovement.startSearch')}</TextNoOptionsAvailable>
      );
    }
    return (
      <TextNoOptionsAvailable>{translate('modalFluidMovement.noInstitution')}</TextNoOptionsAvailable>
    );
  }, []);

  const getModalMinSizeStyle = useCallback(() => {
    let minWidth = 700;
    let minHeight = 650;

    if (isDistributor) {
      minWidth = 900;
    }
    if (isMobileOnly) {
      minWidth = 200;
      minHeight = 500;
    }
    return { minWidth: `${minWidth}px`, minHeight: `${minHeight}px` };
  }, [isDistributor]);

  if (isCheckingStatus || !tdConnectionStatus || isLoading) {
    return (
      <SkeletonForm />
    );
  }

  return (
    <form autoComplete="off" style={getModalMinSizeStyle()} onSubmit={handleSubmit(handleValidateModal)}>
      <ModalHeader onClose={onClose}>
        <CustomIcon icon={<FontAwesomeIcon icon={faTint} />} marginRight="1rem" />
        {translate('modalFluidMovement.editFluidMovement')}
      </ModalHeader>

      <DialogContent style={{ paddingTop: '10px' }}>
        <FlexContainer flexDirection="column">
          <Controller
                control={control}
                name="bottle"
                render={({ field }) => (
                  <TextField
                        error={!!errors.bottle}
                        label={translate('modalFluidMovement.bottleIdentifier')}
                        name="bottle"
                        required
                        style={{ margin: '5px 0' }}
                        value={field.value?.label || ''}
                        onChange={(e) => setValue('bottle', { ...formValues.bottle, label: e.target.value })}
                    />
                )}
            />

          <TextField
                disabled
                error={!!errors.supplierOrRecipient}
                label={translate('modalFluidMovement.supplierOrRecipient')}
                name="supplierOrRecipient"
                style={{ margin: '5px 0' }}
                value={formValues.supplierOrRecipient?.label || ''}
            />

          <Question label={`${translate('common.fluid')} :`}>
            <Controller
                  control={control}
                  name="fluid"
                  render={({ field }) => (
                    <Select
                          {...field}
                          id="selectFluid"
                          isClearable
                          isDisabled={defaultValues.hashId}
                          isSearchable
                          menuPortalTarget={document.body}
                          noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                          options={fluidOptions}
                          placeholder={translate('common.searchByFluidType')}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                          value={field.value || ''}
                          onChange={(value) => setValue('fluid', value)}
                      />
                  )}
              />
            <RequiredSelectHack value={formValues.fluid || ''} />
          </Question>

          <FormLine>
            <Controller
                  control={control}
                  name="measuredAmount"
                  render={({ field }) => (
                    <TextField
                          {...field}
                          error={!!errors.measuredAmount}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">{translate('common.weightUnit')}</InputAdornment>
                          }}
                          inputProps={{
                            step: '0.01',
                            min: currentMovement?.bsffIdentifier ? 1 : 0
                          }}
                          label={translate('common.measuredQuantity')}
                          name="measuredAmount"
                          required
                          style={{ marginTop: '.5rem' }}
                          type="number"
                      />
                  )}
              />
            <CustomTooltip text={translate('modalFluidMovement.tooltipMeasured')} />
          </FormLine>

          <FormLine>
            <Controller
                  control={control}
                  name="movementDate"
                  render={({ field: { value, onChange } }) => (
                    <Datepicker
                          dataCy="createFluidMovementDate"
                          disableFuture
                          format={DATE_FORMAT}
                          label="common.receptionDate"
                          required
                          style={{ marginTop: '.5rem' }}
                          value={value}
                          onChange={onChange}
                      />
                  )}
              />
          </FormLine>

          {currentMovement?.movementType?.value === 'PENDING_TRANSPORT' && (
            <Controller
                    control={control}
                    name="transporterImat"
                    render={({ field }) => (
                      <TextField
                            {...field}
                            error={!!errors.transporterImat}
                            label={translate('common.transporterImat')}
                            name="transporterImat"
                            required
                        />
                    )}
                />
            )}

          <Question label={`${translate('common.operation')} :`}>
            <Controller
                  control={control}
                  name="operationCode"
                  render={({ field }) => (
                    <Select
                          {...field}
                          id="selectOperation"
                          isClearable
                          isSearchable
                          menuPortalTarget={document.body}
                          noOptionsMessage={() => translate('warnings.noOptionsAvailable')}
                          options={operationOptions}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                          value={field.value || ''}
                          onChange={(value) => setValue('operationCode', value)}
                      />
                  )}
              />
            <RequiredSelectHack value={formValues.operationCode || ''} />
          </Question>

          {currentMovement?.movementType?.value === 'PENDING_TRANSPORT' && (
            <Controller
                    control={control}
                    name="transporterImat"
                    render={({ field }) => (
                      <TextField
                            {...field}
                            error={!!errors.transporterImat}
                            label={translate('common.transporterImat')}
                            name="transporterImat"
                            required
                        />
                    )}
                />
            )}

          {formValues.operationCode?.value && ['R12', 'R13', 'D13', 'D14', 'D15'].includes(formValues.operationCode.value) && (
            <Question label={translate('common.nextRecipient')}>
              <Controller
                      control={control}
                      name="nextRecipient"
                      render={({ field }) => (
                        <AsyncSelect
                              {...field}
                              cacheOptions
                              defaultOptions={suppliersOrRecipients}
                              getOptionLabel={option => option.label}
                              getOptionValue={option => option.value}
                              id="selectInstitution"
                              inputValue={searchSuppliersOrRecipients}
                              isClearable
                              isLoading={isLoadingSuppliersOrRecipients}
                              isSearchable
                              loadingMessage={() => translate('modalFluidMovement.loadingInstitutions')}
                              loadOptions={debouncedSuppliersOrRecipientsOptions}
                              menuPlacement={isMobileOnly ? 'top' : 'bottom'}
                              menuPortalTarget={document.body}
                              noOptionsMessage={() => displayNoOptionAvailable(searchSuppliersOrRecipients)}
                              placeholder={translate('common.searchByNameSiret')}
                              styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                              value={field.value || null}
                              onChange={(select) => setValue('nextRecipient', select)}
                              onInputChange={value => setSearchSuppliersOrRecipients(value)}
                          />
                      )}
                  />
              <RequiredSelectHack value={formValues.nextRecipient || ''} />
            </Question>
            )}

          {formValues.operationCode?.value === 'X' && (
            <Question label={`${translate('modalFluidMovement.denyComment')} :`}>
              <Controller
                      control={control}
                      name="comment"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              InputLabelProps={{ required: false }}
                              label={translate('modalFluidMovement.denyCommentLabel')}
                              multiline
                              name="comment"
                              required
                              rows={3}
                          />
                      )}
                      rules={{ required: true }}
                  />
            </Question>
            )}
        </FlexContainer>
      </DialogContent>

      <DialogActions>
        <Button type="button" onClick={onClose}>{translate('button.cancel')}</Button>
        <Button
              color="primary"
              type="submit"
          >
          {translate('button.save')}
        </Button>
      </DialogActions>
    </form>
  );
};
